import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';

import { useMapData } from '../../../map-data/use-map-data';
import { useComparisonProviderContext } from '../../../providers/ComparisonProvider';
import type { PointOfInterestRecord } from '../../../../lcpn-uploader/types';
import { Typography } from '../../Typography';
import { theme } from '../../../styles/theme';

import { DataDisplayTable } from './DataTable';
import { usePoiDataTableDefinitions } from './UsePoiDataTableDefinitions';
import { ComparisonWarning } from './ComparisonWarning';

export function PoiComparisonTable() {
  const { t } = useTranslation();
  const { studiesById } = useMapData();
  const [studies, setStudies] = useState<PointOfInterestRecord[]>([]);
  const [invalidStudyIds, setInvalidStudyIds] = useState<string[]>([]);
  const [duplicateStudyIds, setDuplicateStudyIds] = useState<string[]>([]);
  const { poiComparison, setPoiComparison } = useComparisonProviderContext();

  const { headings, sections } = usePoiDataTableDefinitions();

  useEffect(() => {
    if (!!studiesById && !!poiComparison) {
      const validStudies: PointOfInterestRecord[] = [];
      const invalidIds: string[] = [];
      const duplicateIds: string[] = [];
      const seenIds = new Set<string>();

      for (const studyId of poiComparison.studyIds) {
        if (seenIds.has(studyId)) {
          if (!duplicateIds.includes(studyId)) {
            duplicateIds.push(studyId);
          }
          continue;
        }

        const study = studiesById[studyId];
        if (study) {
          validStudies.push({ id: studyId, ...study });
          seenIds.add(studyId);
        } else {
          invalidIds.push(studyId);
        }
      }

      setStudies(validStudies);

      // Only update warnings if we actually found issues
      if (invalidIds.length > 0 || duplicateIds.length > 0) {
        setInvalidStudyIds(prev => (prev.length === 0 ? invalidIds : prev));
        setDuplicateStudyIds(prev => (prev.length === 0 ? duplicateIds : prev));
      }

      // If we have invalid or duplicate IDs, update context to only include unique valid ones
      if (
        (invalidIds.length > 0 || duplicateIds.length > 0) &&
        validStudies.length > 0
      ) {
        setPoiComparison({
          ...poiComparison,
          studyIds: validStudies.map(s => s.id),
        });
      } else if (validStudies.length === 0 && invalidIds.length > 0) {
        setPoiComparison({ ...poiComparison, studyIds: [] });
      }
    }
  }, [poiComparison, setPoiComparison, studiesById]);

  return (
    <Stack spacing={4}>
      <div style={{ color: '#155181' }}>
        <Typography variant={'h2'}>
          <strong>{t('comparisonTable.poi.title')}:</strong>
          <span>
            <br />
            {t('comparisonTable.poi.subtitle')}
          </span>
        </Typography>
      </div>
      {(invalidStudyIds.length > 0 || duplicateStudyIds.length > 0) && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: theme.spacing(4),
            borderRadius: theme.spacing(1),
          }}
        >
          <ComparisonWarning
            message="The following study IDs were not found and have been removed from the comparison:"
            items={invalidStudyIds}
          />
          <ComparisonWarning
            message="The following duplicate study IDs have been removed:"
            items={duplicateStudyIds}
            marginTop={invalidStudyIds.length > 0 ? theme.spacing(4) : 0}
          />
        </div>
      )}
      {studies.length >= 2 && (
        <DataDisplayTable<PointOfInterestRecord>
          headings={headings}
          type={'poi'}
          items={studies}
          sections={sections}
        />
      )}
      {studies.length < 2 && (
        <div
          style={{
            textAlign: 'center',
            padding: theme.spacing(8),
            color: '#666',
          }}
        >
          <Typography variant={'p1'}>
            {studies.length === 0
              ? 'No valid studies to compare. Please check the study IDs in the URL.'
              : 'At least 2 valid studies are required for comparison.'}
          </Typography>
        </div>
      )}
    </Stack>
  );
}
